import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Card, CardBody, CardHeader, Col, Row, Collapse } from 'reactstrap';
import Swal from 'sweetalert2';
import axios from 'axios';
import apiResponseHandler from '../../../../utils/apiResponseHandler';
import { environment } from '../../../../environment/environment';
import EmployeeLimits from '../../Advances/EmployeeEWALimits';
import { useEnabledFeatureFlag } from '../../../../utils/feature-flagging/FeatureFlagProvider';
import FeatureFlags from '../../../../utils/feature-flagging/FeatureFlags';

const REASONS = [
  {
    value: 1,
    label: '1. Transport',
  },
  {
    value: 2,
    label: '2. Groceries',
  },
  {
    value: 3,
    label: '3. Bills',
  },
  {
    value: 4,
    label: '4. Family/Kids',
  },
  {
    value: 5,
    label: '5. Shopping/entertainment',
  },
  {
    value: 6,
    label: '6. Emergency medical/repair expense',
  },
  {
    value: 7,
    label: '7. Other',
  },
];

/**
 * Component for adding advances to an employee
 * @param {Object} props - Component props
 * @param {string} props.employeeId - ID of the employee
 */
const EmployeeAdvanceAdd = ({ employeeId }) => {
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [employee, setEmployee] = useState();
  const [employeeAdvanceData, setEmployeeAdvanceData] = useState({
    available_advance_balance: 0,
    available_advance_to_withdraw: 0,
    is_advance_permitted: false,
    permission_reason: 'Employee data is not complete',
    volume_and_value: {
      amount_used: 0,
      request_used: 0
    },
    instant: {
      amount_used: 0,
      request_used: 0
    },
    standard: {
      amount_used: 0,
      request_used: 0
    }
  });
  const [employeeLimits, setEmployeeLimits] = useState(null);
  const [amountRequested, setAmountRequested] = useState();
  const [status, setStatus] = useState();
  const [reason, setReason] = useState();
  const [bankDetailsOpen, setBankDetailsOpen] = useState(true);
  const isDisplayNewEwaLimitsEnabled = useEnabledFeatureFlag(
    FeatureFlags.DISPLAY_NEW_EWA_LIMITS
  );

  useEffect(() => {
    getEmployeeAdvanceData(employeeId);
    getEmployeeData(employeeId);
  }, [employeeId]);

  useEffect(() => {
  }, [employeeLimits]);

  const getEmployeeData = (id) => {
    setLoading(true);
    axios.get(`${environment.baseUrl}/employees/${id}`).then((res) => {
      setEmployee(res.data);
      setLoading(false);
      if (res.data?.employer) {
        getEmployeeLimits(id, res.data.employer.id);
      }
    });
  };

  const getEmployeeAdvanceData = (employeeId) => {
    setLoading(true);
    axios
      .get(`${environment.baseUrl}/employees/${employeeId}/advances/`)
      .then((res) => {
        setEmployeeAdvanceData({
          available_advance_balance: Number(res.data.available_advance_balance) || 0,
          available_advance_to_withdraw: Number(res.data.available_advance_to_withdraw) || 0,
          is_advance_permitted: res.data.is_advance_permitted || false,
          permission_reason: res.data.permission_reason || 'Employee data is not complete',
          volume_and_value: {
            amount_used: Number(res.data.volume_and_value?.amount_used) || 0,
            request_used: Number(res.data.volume_and_value?.request_used) || 0
          },
          value_added_service: {
            amount_used: Number(res.data.value_added_service?.amount_used) || 0,
            request_used: Number(res.data.value_added_service?.request_used) || 0
          },
          instant: {
            amount_used: Number(res.data.instant?.amount_used) || 0,
            request_used: Number(res.data.instant?.request_used) || 0
          },
          standard: {
            amount_used: Number(res.data.standard?.amount_used) || 0,
            request_used: Number(res.data.standard?.request_used) || 0
          }
        });
        setLoading(false);
      })
      .catch((error) => {
        let permissionErrorToDisplay = "Server error: cannot retrieve employee data";
        // Only set the message if it is not a backend server error
        if (error.response.status >= 400 && error.response.status < 500) {
          permissionErrorToDisplay = error.response?.message ?? permissionErrorToDisplay;
        }

        setEmployeeAdvanceData({
          available_advance_balance: 0,
          available_advance_to_withdraw: 0,
          is_advance_permitted: false,
          permission_reason: permissionErrorToDisplay,
          volume_and_value: {
            amount_used: 0,
            request_used: 0
          },
          value_added_service: {
            amount_used: 0,
            request_used: 0
          },
          instant: {
            amount_used: 0,
            request_used: 0
          },
          standard: {
            amount_used: 0,
            request_used: 0
          }
        });
        setLoading(false);
      });

  };

  const getEmployeeLimits = (employee_id, employer_id) => {
    axios
      .get(`${environment.baseUrl}/odp_limits/config/`, {
        params: { employer_id, employee_id }
      })
      .then((res) => {
        if (res.data && Object.keys(res.data).length > 0) {
          const limits = {
            ...res.data?.config,
            employee_limits: {
              available_advance_balance: Number(res.data?.employee_limits?.available_advance_balance) || 0,
              available_advance_to_withdraw: Number(res.data?.employee_limits?.available_advance_to_withdraw) || 0,
              amount_deducted_next_payslip: Number(res.data?.employee_limits?.amount_deducted_next_payslip) || 0,
              is_advance_permitted: res.data?.employee_limits?.is_advance_permitted,
              permission_reason: res.data?.employee_limits?.permission_reason,
              volume_and_value: {
                amount_used: Number(res.data?.employee_limits?.volume_and_value?.amount_used) || 0,
                requests_used: Number(res.data?.employee_limits?.volume_and_value?.requests_used) || 0
              },
              value_added_service: {
                amount_used: Number(res.data?.employee_limits?.value_added_service?.amount_used) || 0,
                requests_used: Number(res.data?.employee_limits?.value_added_service?.requests_used) || 0
              },
              instant: {
                amount_used: Number(res.data?.employee_limits?.instant?.amount_used) || 0,
                requests_used: Number(res.data?.employee_limits?.instant?.requests_used) || 0
              },
              standard: {
                amount_used: Number(res.data?.employee_limits?.standard?.amount_used) || 0,
                requests_used: Number(res.data?.employee_limits?.standard?.requests_used) || 0
              }
            }
          };
          setEmployeeLimits(limits ?? {}, employeeAdvanceData);
        } else {
          setEmployeeLimits({});
        }
      })
      .catch((error) => {
        apiResponseHandler.handleApiError('Error', error);
        setEmployeeLimits({});
      });
  };

  const createAdvance = () => {
    setIsSubmitting(true);
    axios
      .post(`${environment.baseUrl}/advancerequests/`, {
        employee: employeeId,
        amount_requested: amountRequested,
        status,
        reason,
        source_type: 'manual',
      })
      .then((res) => {
        Swal.fire({
          title: 'Advance request saved',
          icon: 'success',
          toast: true,
          position: 'top',
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
        });
        setIsSubmitting(false);
      })
      .catch((error) => {
        apiResponseHandler.handleApiError('Error', error);
        setIsSubmitting(false);
      });
  };

  return loading ? (
    <Col className="d-flex justify-content-center mt-3">
      <div className="spinner-border">
        <span className="sr-only">Loading...</span>
      </div>
    </Col>
  ) : (
    <div className="p-3">
      <Row>
        <Col md="8" className="m-auto">
          {/* Balances & deductions and Limits are rendered by EmployeeLimits component */}
          {isDisplayNewEwaLimitsEnabled && employeeLimits ? (
            <EmployeeLimits
              employeeLimits={employeeLimits}
              employeeAdvanceData={employeeAdvanceData}
            />
          ) : (
            <Card className="shadow mb-3">
              <CardBody>
                <Row>
                  <Col>
                    <Card>
                      <CardBody>
                        <h3>
                          {employeeAdvanceData?.available_advance_to_withdraw
                            ? `R${employeeAdvanceData.available_advance_to_withdraw}`
                            : '-'}
                        </h3>
                        <small>Available to withdraw</small>
                      </CardBody>
                    </Card>
                  </Col>
                  <Col>
                    <Card>
                      <CardBody>
                        <h3>
                          {employeeAdvanceData?.available_advance_balance
                            ? `R${employeeAdvanceData.available_advance_balance}`
                            : '-'}
                        </h3>
                        <small>Available advance balance</small>
                      </CardBody>
                    </Card>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          )}

          {/* Bank details card */}
          <Card className="shadow mb-3">
            <CardHeader className="d-flex justify-content-between align-items-center" onClick={() => setBankDetailsOpen(!bankDetailsOpen)}>
              <span className="font-weight-bold">Bank details</span>
              <i className={`fa fa-chevron-${bankDetailsOpen ? 'up' : 'down'}`} />
            </CardHeader>
            <Collapse isOpen={bankDetailsOpen}>
              <CardBody>
                <div className="limit-item">
                  <div className="limit-label">Bank account number (last 4 digits)</div>
                  <div className="limit-value">
                    {employee?.bank_account_number
                      ? employee.bank_account_number.slice(-4)
                      : '-'}
                  </div>
                </div>
              </CardBody>
            </Collapse>
          </Card>

          {/* Warning alert */}
          {employeeAdvanceData &&
            !employeeAdvanceData.is_advance_permitted && (
              <div className="alert alert-warning mb-3" role="alert">
                <strong>Warning!</strong>{' '}
                {employeeAdvanceData.permission_reason}
              </div>
            )}


          <Card className="shadow mt-3">
            <CardBody>
              <Row>
                <Col>
                  <span className="has-float-label">
                    <input
                      type="number"
                      name="amount_requested"
                      placeholder="Amount"
                      onChange={(event) =>
                        setAmountRequested(event.target.value)
                      }
                      className="form-control"
                      id="amount_requested"
                      required
                    />
                  </span>
                </Col>
                <Col>
                  <select
                    className="form-control"
                    name="status"
                    onChange={(event) => setStatus(event.target.value)}
                    onBlur={(event) => setStatus(event.target.value)}
                    required
                  >
                    <option value="pending" selected>
                      Pending
                    </option>
                    <option value="rejected">Rejected</option>
                    <option value="accepted">Accepted</option>
                    <option value="paid">Paid</option>
                  </select>
                </Col>
                <Col>
                  <select
                    className="form-control"
                    name="reason"
                    onChange={(event) => setReason(event.target.value)}
                    onBlur={(event) => setReason(event.target.value)}
                    required
                  >
                    {REASONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </Col>
              </Row>
              <Row className="mt-2">
                <Col>
                  <button
                    type="submit"
                    className="btn btn-primary float-right"
                    onClick={createAdvance}
                    disabled={
                      !amountRequested ||
                      !status ||
                      !reason ||
                      isSubmitting ||
                      !employeeAdvanceData.is_advance_permitted
                    }
                  >
                    Add advance
                  </button>
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

EmployeeAdvanceAdd.propTypes = {
  employeeId: PropTypes.string.isRequired
};

export default EmployeeAdvanceAdd;
